import { useState, useEffect } from 'react';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuth } from '../contexts/AuthContext';
import { generateSecureToken } from '../utils/security';

export default function TokenGenerator() {
  const [token, setToken] = useState('');
  const [loading, setLoading] = useState(false);
  const { currentUser } = useAuth();

  // Carregar token existente
  useEffect(() => {
    const loadExistingToken = async () => {
      try {
        const tokenDoc = await getDoc(doc(db, 'config', 'cronToken'));
        if (tokenDoc.exists()) {
          setToken(tokenDoc.data().token);
        }
      } catch (error) {
        console.error('Erro ao carregar token:', error);
      }
    };
    loadExistingToken();
  }, []);

  const generateToken = async () => {
    if (!currentUser?.role) {
      console.error('Usuário sem role definida');
      alert('Erro: Usuário sem permissões necessárias');
      return;
    }

    if (currentUser.role !== 'admin') {
      console.error('Usuário não é admin:', currentUser.role);
      alert('Apenas administradores podem gerar tokens');
      return;
    }

    setLoading(true);
    try {
      const newToken = generateSecureToken();
      console.log('Token gerado com sucesso');
      
      // Salvar no Firestore
      await setDoc(doc(db, 'config', 'cronToken'), {
        token: newToken,
        updatedAt: new Date().toISOString(),
        updatedBy: currentUser.uid
      });

      setToken(newToken);
      console.log('Token salvo no Firestore');
    } catch (error) {
      console.error('Erro ao gerar token:', error);
      alert('Erro ao gerar token. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(token);
    alert('Token copiado para a área de transferência!');
  };

  if (!currentUser) {
    return <div>Carregando...</div>;
  }

  if (currentUser.role !== 'admin') {
    return <div>Acesso negado. Apenas administradores podem acessar esta página.</div>;
  }

  // Usar a URL base do Vite em desenvolvimento
  const baseUrl = process.env.NODE_ENV === 'production' 
    ? window.location.origin 
    : 'http://localhost:3001';

  const cronUrl = `${baseUrl}/api/cron?token=`;

  const testEndpoint = async () => {
    try {
      const response = await fetch(`${cronUrl}${token}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json'
        },
        mode: 'no-cors' // Adiciona esta opção para evitar erros de CORS
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      alert(data.message || 'Endpoint testado com sucesso!');
    } catch (error) {
      console.error('Erro ao testar endpoint:', error);
      alert('Endpoint testado! Verifique o console do servidor para ver os resultados.');
    }
  };

  return (
    <div className="p-6 bg-white rounded-lg shadow-md">
      <h1 className="text-2xl font-bold mb-6">Gerador de Token para Cron Jobs</h1>
      
      <div className="space-y-4">
        <p className="text-gray-600">
          Este token é usado para autenticar as chamadas dos cron jobs.
          Gere um novo token sempre que precisar atualizar a segurança.
        </p>

        <button
          onClick={generateToken}
          disabled={loading}
          className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary-dark disabled:opacity-50"
        >
          {loading ? 'Gerando...' : 'Gerar Novo Token'}
        </button>

        {token && (
          <div className="mt-6 space-y-4">
            <div className="p-4 bg-gray-100 rounded-lg break-all">
              <p className="font-mono">{token}</p>
            </div>
            
            <button
              onClick={copyToClipboard}
              className="text-primary hover:text-primary-dark"
            >
              Copiar Token
            </button>

            <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <h3 className="font-bold text-yellow-800 mb-2">Instruções:</h3>
              <ol className="list-decimal list-inside space-y-2 text-yellow-700">
                <li>Copie este token</li>
                <li>Adicione ao arquivo .env como CRON_SECRET</li>
                <li>Configure o cron job para chamar: <code className="bg-yellow-100 px-2 py-1 rounded">{cronUrl}seu_token</code></li>
                <li>Mantenha este token em segredo</li>
              </ol>
            </div>

            <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <h3 className="font-bold text-blue-800 mb-2">Exemplo de configuração no cPanel:</h3>
              <pre className="bg-blue-100 p-3 rounded-lg overflow-x-auto text-sm">
                {`* * * * * curl "${cronUrl}${token}"`}
              </pre>
              <p className="mt-2 text-blue-700 text-sm">
                Esta configuração executará o cron job a cada minuto.
              </p>
            </div>

            <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
              <h3 className="font-bold text-green-800 mb-2">Testar Manualmente:</h3>
              <p className="text-green-700 mb-2">
                Para testar o endpoint manualmente, clique no botão abaixo:
              </p>
              <button
                onClick={testEndpoint}
                className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
              >
                Testar Endpoint
              </button>
              <p className="mt-2 text-sm text-green-600">
                URL do endpoint: <code className="bg-green-100 px-2 py-1 rounded">{cronUrl}{token}</code>
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
